import { MA_TYPE } from "../indicatorTypes";
import { isAbove } from "./comparisons";
import { fillMovingAverage } from "./movingAverages";
import { fillAverageTrueRange } from "./trueRange";
import { valuesReader } from "./priceSources";

const PERCENT = 100;
const EPSILON = 1e-10;

// Directional movement of a bar as TV Std.dmi: the larger move of the high
// up or the low down counts, the other one is zero; none on the first bar
const plusMoveReader = (bars) => (index) => {
  if (index < 1) return NaN;

  const up = bars[index].high - bars[index - 1].high;
  const down = bars[index - 1].low - bars[index].low;

  return isAbove(up, down) && isAbove(up, 0) ? up : 0;
};

const minusMoveReader = (bars) => (index) => {
  if (index < 1) return NaN;

  const up = bars[index].high - bars[index - 1].high;
  const down = bars[index - 1].low - bars[index].low;

  return isAbove(down, up) && isAbove(down, 0) ? down : 0;
};

const smooth = (read, target, period, fromIndex, toIndex) =>
  fillMovingAverage({ type: MA_TYPE.SMMA, read, target, period, fromIndex, toIndex });

// TV Std.fixnan: a value that cannot be calculated repeats the previous one
const fixNan = (value, previous) => (Number.isNaN(value) && previous !== undefined ? previous : value);

/**
 * +DI, −DI and ADX as TV Std.dmi (study Directional Movement Index): Wilder
 * (RMA) averages of the directional movement over the ATR, ADX is the RMA
 * of DX = |+DI − −DI| / (+DI + −DI).
 *
 * @param {object} params
 * @param {object[]} params.bars
 * @param {number} params.diLength
 * @param {number} params.adxSmoothing
 * @param {Object<string, number[]>} params.series - plusDi, minusDi, adx and the
 *   helper arrays atr, plusMove, minusMove, dx; values before `fromIndex` stay
 * @param {number} params.fromIndex
 */
export const fillDirectionalMovement = ({ bars, diLength, adxSmoothing, series, fromIndex }) => {
  const toIndex = bars.length;

  fillAverageTrueRange({ bars, target: series.atr, period: diLength, fromIndex });
  smooth(plusMoveReader(bars), series.plusMove, diLength, fromIndex, toIndex);
  smooth(minusMoveReader(bars), series.minusMove, diLength, fromIndex, toIndex);

  for (let index = fromIndex; index < toIndex; index += 1) {
    const plusDi = fixNan((PERCENT * series.plusMove[index]) / series.atr[index], series.plusDi[index - 1]);
    const minusDi = fixNan((PERCENT * series.minusMove[index]) / series.atr[index], series.minusDi[index - 1]);
    const sum = plusDi + minusDi;

    series.plusDi[index] = plusDi;
    series.minusDi[index] = minusDi;
    series.dx[index] = (Math.abs(plusDi - minusDi) / (Math.abs(sum) <= EPSILON ? sum + 1 : sum)) * PERCENT;
  }

  smooth(valuesReader(series.dx), series.adx, adxSmoothing, fromIndex, toIndex);
};
